import {
  AccordionSummary,
  Box,
  Chip,
  ChipProps,
  Icon,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {alpha} from "@mui/material/styles";
import {Grid} from "@mui/system";
import pxToRem from "hooks/usePxToRem";
import {CustomIconButton} from "ui";
import {
  ID_COLLECT_VOYAGE,
  ID_DELIVERY_VOYAGE,
  ID_FINAL_DESTINATION_VOYAGE,
  ID_INITIAL_VOYAGE,
} from "views/ProcessDetails/utils/constants";
import {Voyage} from "views/ProcessDetails/refactor/types/voyages.types";
import {Modality} from "util/globalEnums";
import {getFormattedDateFromValue} from "util/getISODate";
import {shouldDisableVoyageEditByCreatedAtTime} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/utils/shouldDisableVoyage";

type Props = {
  index: number;
  voyage: Voyage;
  modality: Modality;
  onDeleteVoyage: (
    e: React.MouseEvent<HTMLElement>,
    voyage: Voyage,
    index: number,
  ) => void;
  cancelledProcess: boolean;
  isAiProcess: boolean;
  isDirty: boolean;
  isDraft: boolean;
};

const VoyageSummary = (props: Props) => {
  const {
    index,
    voyage,
    modality,
    onDeleteVoyage,
    cancelledProcess,
    isAiProcess,
    isDirty,
    isDraft,
  } = props;

  const hasUnsavedChanges = isDraft || isDirty;

  const getVoyageTypeIcon = (voyage: Voyage, modality: Modality) => {
    if (voyage.typeVoyage?.id === ID_COLLECT_VOYAGE) {
      return "local_shipping";
    }

    if (voyage.typeVoyage?.id === ID_DELIVERY_VOYAGE) {
      return "local_shipping";
    }

    if (voyage.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE) {
      return "local_shipping";
    }

    if (modality === Modality.Aérea) {
      return "flight";
    }
    return "directions_boat";
  };

  const getFormattedDate = (date: string | null) => {
    if (!date) return "-";
    return getFormattedDateFromValue(date, "dd/MM/yyyy");
  };

  const getLoadOrEstimatedDate = (
    voyage: Voyage,
  ): {type: string; date: string} => {
    if (voyage.cargoLoad) {
      return {type: `Embarque: `, date: getFormattedDate(voyage.cargoLoad)};
    }
    return {type: `ETD: `, date: getFormattedDate(voyage.estimatedCargoLoad)};
  };

  const getVoyageStatus = (
    voyage: Voyage,
  ): {status: string; color: ChipProps["color"]} => {
    if (voyage.cargoUnload) {
      return {status: "Desembarcada", color: "success"};
    }

    if (voyage.cargoLoad) {
      return {status: "Embarcada", color: "primary"};
    }
    return {status: "Não embarcada", color: "default"};
  };

  const getUnloadOrEstimatedDate = (
    voyage: Voyage,
  ): {type: string; date: string} => {
    if (voyage.cargoUnload) {
      return {
        type: `Desembarque: `,
        date: getFormattedDate(voyage.cargoUnload),
      };
    }
    return {
      type: `ETA: `,
      date: getFormattedDate(voyage.estimatedCargoUnload),
    };
  };

  return (
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls="panel1bh-content"
      id="panel1bh-header"
      sx={
        hasUnsavedChanges
          ? {
              borderLeft: (theme) => `3px solid ${theme.palette.warning.main}`,
              backgroundColor: (theme) =>
                alpha(theme.palette.warning.main, 0.06),
            }
          : undefined
      }
    >
      <Grid container width="100%">
        <Grid
          size={0.5}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Chip label={index + 1} color="primary" size="small" />
        </Grid>
        <Grid
          size={0.5}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon>{getVoyageTypeIcon(voyage, modality)}</Icon>
        </Grid>
        <Grid size={10.5}>
          <Box display="flex" gap={pxToRem(4)} alignItems="center" flexWrap="wrap">
            <Typography variant="body1" component="span">
              {`${voyage.origin?.name ?? "-"} -> ${voyage.destination?.name ?? "-"}`}
            </Typography>
            {!isAiProcess && (
              <Chip
                label={voyage.vessel?.name ?? "-"}
                color="primary"
                size="small"
              />
            )}
            {hasUnsavedChanges && (
              <Chip
                label={isDraft ? "Nova" : "Alterada"}
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
            <Chip
              label={getVoyageStatus(voyage).status}
              color={getVoyageStatus(voyage).color}
              size="small"
            />
            {isAiProcess && voyage.typeService && (
              <Chip
                label={`Serviço: ${voyage.typeService}`}
                color="primary"
                size="small"
              />
            )}
          </Box>

          <Typography variant="caption" component="div">
            <Typography variant="caption" component="span" fontWeight={500}>
              <strong>{`${voyage.typeVoyage?.name ?? "-"}`}</strong>
            </Typography>
            {` - `}
            <strong>{getLoadOrEstimatedDate(voyage).type}</strong>
            {`${getLoadOrEstimatedDate(voyage).date}`}
            {` -> `}
            <strong>{getUnloadOrEstimatedDate(voyage).type}</strong>
            {`${getUnloadOrEstimatedDate(voyage).date}`}
          </Typography>
        </Grid>
        <Grid
          size={0.5}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CustomIconButton
            component="span"
            color="error"
            tooltipText="Apagar viagem"
            iconName="delete"
            disabled={
              cancelledProcess ||
              voyage.typeVoyage?.id === ID_INITIAL_VOYAGE ||
              shouldDisableVoyageEditByCreatedAtTime(voyage.createdAt)
            }
            onClick={(e) => {
              e.stopPropagation();
              onDeleteVoyage(e, voyage, index);
            }}
          />
        </Grid>
      </Grid>
    </AccordionSummary>
  );
};

export default VoyageSummary;
